import { Combobox } from "@/components/ui/combobox";
import { Acts } from "api/types";
import useActsLookup from "hooks/queries/useActsLookup";
import useGetActsPriceInsurer from "hooks/queries/useGetActsPriceInsurer";
import useGetInvoices from "hooks/queries/useGetInvoices";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { generateId } from "utils/generateId";
import { cn } from "@/lib/utils";
import { inputClass } from "@/lib/formStyles";
import { getStoragePatient, getUserData } from "../../../utils/storageUtils";
import { openProtectedFile } from "../../../utils/openProtectedFile";
import AccordionCustom from "../../legacy/Accordion";
import useMutateCreateInvoice from "hooks/mutations/useMutateCreateInvoice";
import FedaPayButton from "../../Payment/FedaPayButton";
import FinancialTransactionModal from "../../Payment/FinancialTransactionModal";

const storagePatient = getStoragePatient();
const storageUser = getUserData();

// Variante de `inputClass` (cf. `@/lib/formStyles`) — `mt-0` pour
// retirer la respiration baked in (on est dans une cellule de table,
// pas sous un <span> label) et `text-right` pour aligner les nombres.
const numberInputClass = cn(inputClass, "mt-0 text-right");

// `createdAt` côté back est un ISO complet (ex. "2026-05-25T23:39:42.045543").
// On affiche jour/mois/année en FR — l'heure n'a pas d'intérêt pour l'utilisateur
// dans l'aperçu de la facture. Pattern aligné sur TabTwo.
const formatInvoiceDate = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const TabThree = ({ current_patient: curr_patient }: any) => {
  const [openForm, setOpenForm] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const current_patient = Object.keys(curr_patient).length
    ? curr_patient
    : storagePatient;

  const [form, setForm] = useState({
    invoiceActsRequests: [
      {
        id: 0,
        actsId: 0,
        amountPaid: 0,
        insurerCoverage: 0,
        invoiceAmount: 0,
        // `price` (P.U.) doit figurer dans l'état initial pour que TS
        // infère le shape de chaque ligne avec ce champ — sinon l'édition
        // manuelle du P.U. (cf. cellule unitPrice) plante au typecheck.
        price: 0,
        quantity: 1,
        remainingPaid: 0,
        totalInvoice: 0,
      },
    ],
    patientId: current_patient?.id,
    practitionerId: storageUser?.user?.id,
  });

  useEffect(() => {
    setForm((prevForm) => ({
      ...prevForm,
      practitionerId: storageUser?.user?.id,
      patientId: current_patient?.id,
    }));
  }, [current_patient?.id]);

  const mutation = useMutateCreateInvoice({ patientId: current_patient?.id });
  const { data } = useGetInvoices(current_patient?.id);
  const { data: invoices } = data || {};

  const { data: actsPriceInsurerData } = useGetActsPriceInsurer(
    current_patient?.insurer?.id
  );
  // Unwrap explicitly. apiRequest returns `{ok, status, data, ...}` and
  // a silent `|| {}` would let an insurer-covered patient be billed at
  // the catalog price on any fetch failure — surface the failure instead.
  // `ApiResponse` (api/types.ts) prédate l'envelope `ok/status` — cast
  // localement plutôt que d'élargir le type partagé.
  const actsPriceInsurerEnvelope = actsPriceInsurerData as
    | { ok?: boolean; data?: any[] }
    | undefined;
  const acts_price_insurer = actsPriceInsurerEnvelope?.ok
    ? actsPriceInsurerEnvelope.data
    : undefined;
  const insurerTarifsUnavailable =
    current_patient?.insurer?.id != null &&
    actsPriceInsurerEnvelope != null &&
    actsPriceInsurerEnvelope.ok === false;

  // Acts : lookup léger (id, label, price, actsTypeName) cachée 30 min
  // — pas l'endpoint paginé /acts. `price` / `actsTypeName` sont
  // nécessaires au calcul des lignes de facture (fallback sans assureur
  // + choix du % couverture EXAM vs consultation).
  const { data: acts = [] } = useActsLookup();

  // Met à jour un champ d'une ligne. Construit un nouveau tableau plutôt
  // que de muter `data` en place — l'ancienne version partageait la même
  // référence entre `form.invoiceActsRequests` et le `data` passé en
  // paramètre, ce qui rendait le diff React aléatoire.
  const handleArrayChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    { id, name }: { id: number; data: any; name: string }
  ) => {
    setForm((prev) => {
      const list: any[] = (prev as any)[name] || [];
      const next = list.map((row: any) =>
        row.id === id ? { ...row, [e.target.name]: e.target.value } : row
      );
      return { ...prev, [name]: next };
    });
  };

  const invoiceItem = {
    id: 0,
    actsId: 0,
    amountPaid: 0,
    insurerCoverage: 0,
    invoiceAmount: 0,
    price: 0,
    quantity: 1,
    remainingPaid: 0,
    totalInvoice: 0,
  };

  const handleAddInvoiceItem = (e: React.MouseEvent) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      invoiceActsRequests: [
        ...prev.invoiceActsRequests,
        { ...invoiceItem, id: generateId(prev.invoiceActsRequests) },
      ],
    }));
  };

  const handleRemoveInvoiceItem = (e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    setForm((prev) => ({
      ...prev,
      invoiceActsRequests: prev.invoiceActsRequests.filter((i) => i.id !== idx),
    }));
  };

  const [localActs, setLocalActs] = useState<any>([{ price: 0 }]);

  const mapReturnRequests = (data: any) =>
    data.map((item: any) => ({
      actsId: item.actsId || item?.value,
      amountPaid: item.amountPaid,
      insurerCoverage: item.insurerCoverage,
      price: item.price,
      invoiceAmount: item.invoiceAmount,
      quantity: item.quantity,
      remainingPaid:
        Number(item.invoiceAmount || 0) - Number(item.amountPaid || 0),
      totalInvoice: item.totalInvoice,
    }));

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      invoiceActsRequests: mapReturnRequests(form.invoiceActsRequests),
    });
  };

  const calculateTotalInvoice = (act: any, quantity: any) => {
    if (act?.actsTypeName === Acts.ActsTypeNameEnum.EXAM) {
      return Number(act?.price) * Number(quantity);
    }
    return Number(act?.price) * Number(quantity);
  };

  const calcTotalInvoice = (act: any, quantity: any) => {
    return Math.round(Number(act?.price) * Number(quantity)) || 0;
  };

  const calculateInvoiceAmount = (act: any, quantity: any) => {
    const total = calcTotalInvoice(act, quantity);
    if (act?.actsTypeName === "EXAM") {
      const examPercent = current_patient?.examPercent || 0;
      if (examPercent === 0) {
        return Math.round(act?.price * Number(quantity));
      }
      return Math.round(
        total - act?.price * Number(quantity) * (Number(examPercent) / 100)
      );
    }
    const consultationPercent = current_patient?.consultationPercent || 0;
    if (consultationPercent === 0) {
      return Math.round(act?.price * Number(quantity));
    }
    return Math.round(
      total -
        act?.price * Number(quantity) * (Number(consultationPercent) / 100)
    );
  };

  // Normalisation : le back renvoie `basePrice` sur les actes du catalogue,
  // alors que `acts_price_insurer` (prix négocié) utilise `price`. Tous les
  // calculs de la facture (`calcTotalInvoice`, `calculateInvoiceAmount`…)
  // lisent `act.price` — on alias donc `basePrice` vers `price` ici pour
  // que la colonne P.U. et les calculs marchent même sans assureur.
  const adaptSelectActsOptions = (data?: any) =>
    data?.map((item: any) => ({
      value: item?.id,
      label: item?.label,
      ...item,
      price: item?.price ?? item?.basePrice ?? 0,
    }));

  // L'endpoint `/invoice/by-patient/{id}` est déjà scopé côté back et la
  // queryKey du hook inclut `patientId` — pas de filtre client à
  // refaire. L'ancienne version filtrait sur `invoice.patient.id`, qui
  // n'existe plus depuis que le back renvoie une réponse plate
  // (`patientId`, `patientFirstName`… au top level).
  const filteredInvoices = (invoices || [])
    .slice()
    .sort(
      (a: any, b: any) => +new Date(b.createdAt) - +new Date(a.createdAt)
    );

  return (
    <div className="space-y-6" data-tab="bons">
      {/* Action header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
            Facturation
          </p>
          <h2 className="mt-1 text-lg font-semibold text-slate-800">Bons</h2>
          <p className="text-sm text-slate-500">
            {filteredInvoices.length} facture
            {filteredInvoices.length > 1 ? "s" : ""} pour ce patient
          </p>
        </div>
        {!openForm && (
          <button
            type="button"
            onClick={() => setOpenForm(true)}
            className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
            />
            <i className="bx bx-plus text-lg" />
            Nouvelle facture
          </button>
        )}
      </div>

      {/* Form de création */}
      {openForm && (
        <form className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">
              Nouvelle facture
            </h3>
            <button
              type="button"
              onClick={() => setOpenForm(false)}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              aria-label="Fermer"
            >
              <i className="bx bx-x text-xl" />
            </button>
          </div>

          {insurerTarifsUnavailable && (
            <div
              role="alert"
              className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
            >
              <p className="font-semibold">
                Tarifs assureur indisponibles
              </p>
              <p className="mt-1 text-xs text-rose-600">
                Les tarifs négociés de l'assureur n'ont pas pu être chargés.
                Les actes ajoutés ici seraient facturés au prix catalogue —
                réessayez plus tard ou rechargez la page avant d'enregistrer.
              </p>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                  <th className="px-3 py-3 text-left">Acte</th>
                  <th className="px-3 py-3 text-right">Qté</th>
                  <th className="px-3 py-3 text-right">P.U</th>
                  <th className="px-3 py-3 text-right">% PC</th>
                  <th className="px-3 py-3 text-right">Total</th>
                  <th className="px-3 py-3 text-right">Mt fact</th>
                  <th className="px-3 py-3 text-right">Mt payé</th>
                  <th className="px-3 py-3 text-right">Reste</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {form.invoiceActsRequests.map((item, idx) => (
                  <tr
                    key={item.id}
                    className="border-t border-slate-100 align-middle"
                  >
                    <td className="px-3 py-3 min-w-[260px]">
                      <Combobox
                        options={adaptSelectActsOptions(acts)}
                        value={item?.actsId ?? null}
                        placeholder="Acte"
                        searchPlaceholder="Rechercher un acte..."
                        className="w-full"
                        onChange={(data: any) => {
                          // Tarif négocié assureur : on tolère les deux formes
                          // de réponse (objet imbriqué `acts.id` ou champ plat
                          // `actsId`) et on ne lève jamais quand la liste est
                          // absente (patient sans assureur). Avant, `item.acts.id`
                          // jetait une TypeError dès qu'un tarif assureur existait,
                          // ce qui faisait échouer la sélection de l'acte.
                          const findAct = (acts_price_insurer || []).find(
                            (item: any) =>
                              String(item?.acts?.id ?? item?.actsId) ===
                              String(data?.value)
                          );
                          // On stocke dans localActs la version « résolue » :
                          // tarif négocié assureur s'il existe, sinon le
                          // basePrice du catalogue (déjà aliasé vers `price`
                          // dans adaptSelectActsOptions). Garantit que la
                          // colonne P.U. = la valeur réellement utilisée
                          // dans les calculs.
                          const resolvedAct = findAct
                            ? { ...data, ...findAct, price: findAct.price }
                            : data;
                          setLocalActs((prev: any) => {
                            const next = [...prev];
                            next[idx] = resolvedAct;
                            return next;
                          });
                          setForm((prev) => {
                            const next = [...prev.invoiceActsRequests];
                            next[idx] = {
                              ...data,
                              ...next[idx],
                              actsId: data?.value,
                              totalInvoice: calcTotalInvoice(
                                findAct ? findAct : data,
                                next[idx]?.quantity || 1
                              ),
                              price: calculateTotalInvoice(
                                findAct ? findAct : data,
                                1
                              ),
                              quantity: 1,
                              amountPaid: calculateInvoiceAmount(
                                findAct ? findAct : data,
                                next[idx]?.quantity || 1
                              ),
                              invoiceAmount: calculateInvoiceAmount(
                                findAct ? findAct : data,
                                next[idx]?.quantity || 1
                              ),
                              insurerCoverage:
                                data?.actsTypeName === "EXAM"
                                  ? current_patient?.examPercent
                                  : current_patient?.consultationPercent || 0,
                            };
                            return { ...prev, invoiceActsRequests: next };
                          });
                        }}
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="number"
                        className={numberInputClass}
                        min="1"
                        name="quantity"
                        value={item?.quantity || 1}
                        onChange={(e) => {
                          const newQty = e.target.value;
                          handleArrayChange(e, {
                            id: item.id,
                            data: form.invoiceActsRequests,
                            name: "invoiceActsRequests",
                          });
                          setForm((prev) => {
                            const next = [...prev.invoiceActsRequests];
                            next[idx] = {
                              ...next[idx],
                              totalInvoice: calcTotalInvoice(
                                next[idx],
                                newQty
                              ),
                              amountPaid: calculateInvoiceAmount(
                                next[idx],
                                newQty
                              ),
                              invoiceAmount: calculateInvoiceAmount(
                                next[idx],
                                newQty
                              ),
                            };
                            return { ...prev, invoiceActsRequests: next };
                          });
                        }}
                      />
                    </td>
                    <td className="px-2 py-3">
                      {/* P.U. éditable. Source de vérité = `item.price` du
                         form (consommé par `mapReturnRequests` au submit).
                         À chaque édition on resynchronise `localActs[idx]`
                         (utilisé pour `actsTypeName` ailleurs) ET on
                         recalcule total / mt fact / mt payé pour que les
                         colonnes dérivées reflètent immédiatement le P.U.
                         saisi. */}
                      <input
                        type="number"
                        name="unitPrice"
                        min={0}
                        value={item?.price ?? 0}
                        onChange={(e) => {
                          const newPrice = Number(e.target.value) || 0;
                          setLocalActs((prev: any) => {
                            const next = [...prev];
                            next[idx] = {
                              ...(next[idx] || {}),
                              price: newPrice,
                            };
                            return next;
                          });
                          setForm((prev) => {
                            const next = [...prev.invoiceActsRequests];
                            const row = next[idx];
                            const qty = row?.quantity || 1;
                            const actLike = {
                              ...(row || {}),
                              ...(localActs[idx] || {}),
                              price: newPrice,
                            };
                            next[idx] = {
                              ...row,
                              price: newPrice,
                              totalInvoice: calcTotalInvoice(actLike, qty),
                              invoiceAmount: calculateInvoiceAmount(
                                actLike,
                                qty
                              ),
                              amountPaid: calculateInvoiceAmount(actLike, qty),
                            };
                            return { ...prev, invoiceActsRequests: next };
                          });
                        }}
                        className={numberInputClass}
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="number"
                        name="insurerCoverage"
                        value={item.insurerCoverage}
                        onChange={(e) => {
                          setForm((prev) => {
                            const next = [...prev.invoiceActsRequests];
                            const target = next[idx];
                            next[idx] = {
                              ...target,
                              insurerCoverage:
                                localActs[idx]?.actsTypeName === "EXAM"
                                  ? current_patient?.examPercent
                                  : current_patient?.consultationPercent ||
                                    e.target.value ||
                                    0,
                            };
                            return { ...prev, invoiceActsRequests: next };
                          });
                          handleArrayChange(e, {
                            id: item.id,
                            data: form.invoiceActsRequests,
                            name: "invoiceActsRequests",
                          });
                        }}
                        className={numberInputClass}
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        readOnly
                        type="number"
                        name="totalInvoice"
                        value={item.totalInvoice}
                        className={numberInputClass + " cursor-not-allowed"}
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="text"
                        className={numberInputClass}
                        value={item.invoiceAmount}
                        readOnly
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="text"
                        className={numberInputClass}
                        value={item.amountPaid}
                        name="amountPaid"
                        onChange={(e) => {
                          handleArrayChange(e, {
                            id: item.id,
                            data: form.invoiceActsRequests,
                            name: "invoiceActsRequests",
                          });
                        }}
                      />
                    </td>
                    <td className="px-2 py-3">
                      <input
                        type="text"
                        readOnly
                        className={numberInputClass + " cursor-not-allowed"}
                        value={
                          Number(item.invoiceAmount || 0) -
                          Number(item.amountPaid || 0)
                        }
                        name="remainingPaid"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={(e) => handleRemoveInvoiceItem(e, item.id)}
                        type="button"
                        className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                        aria-label="Supprimer"
                      >
                        <i className="bx bx-trash text-lg" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <button
              onClick={handleAddInvoiceItem}
              type="button"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-50 hover:border-emerald-200"
            >
              <i className="bx bx-plus-circle text-base" />
              Ajouter une ligne
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpenForm(false)}
                className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={insurerTarifsUnavailable}
                onClick={(e) => {
                  setOpenForm(false);
                  handleSubmit(e);
                }}
                className="group/btn relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
                />
                <i className="bx bx-check text-lg" />
                Enregistrer
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Liste */}
      <div className="space-y-3">
        {filteredInvoices.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-12 text-center">
            <i className="bx bx-receipt text-4xl text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-600">
              Aucune facture
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Les factures créées apparaîtront ici.
            </p>
          </div>
        ) : (
          filteredInvoices.map((invoice: any) => {
            // Stockés dans la cellule de récap (ci-dessous) puis lus par
            // le bloc FedaPay — préserve le calcul existant.
            let mtPayeValue = 0;
            let totalRemainingPaid = 0;
            const paymentStatus = invoice?.paymentStatus || "PENDING";

            return (
              <AccordionCustom
                key={invoice.id}
                title={
                  <div className="flex items-center gap-3 flex-wrap min-w-0 w-full">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-2 ring-white">
                      <i className="bx bx-receipt text-base" />
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                        Facturation
                      </span>
                      <span className="text-sm font-semibold text-slate-800 truncate">
                        {formatInvoiceDate(invoice?.createdAt)}
                      </span>
                    </div>
                    {paymentStatus === "PENDING" ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedInvoiceId(invoice?.id);
                          setModalOpen(true);
                        }}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100 transition-colors"
                        title="En attente — cliquer pour voir les transactions"
                      >
                        <i className="bx bx-time-five text-sm" />
                        En attente
                      </button>
                    ) : (
                      <span
                        className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700"
                        title="Payé"
                      >
                        <i className="bx bx-check-circle text-sm" />
                        Payé
                      </span>
                    )}
                  </div>
                }
              >
                <div className="space-y-4 px-4 py-3 sm:px-6">
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                          <th className="px-3 py-3 text-left">Date</th>
                          <th className="px-3 py-3 text-left">Acte</th>
                          <th className="px-3 py-3 text-right">Qté</th>
                          <th className="px-3 py-3 text-right">P.U</th>
                          <th className="px-3 py-3 text-right">% PC</th>
                          <th className="px-3 py-3 text-right">Total</th>
                          <th className="px-3 py-3 text-right">Mt fact</th>
                          <th className="px-3 py-3 text-right">Mt payé</th>
                          <th className="px-3 py-3 text-right">Reste</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.invoiceActsList.map(
                          (item: any, idx: number) => (
                            <tr
                              key={idx}
                              className="border-t border-slate-100 text-slate-600 hover:bg-emerald-50/40 transition"
                            >
                              <td className="px-3 py-3 whitespace-nowrap text-slate-500">
                                {formatInvoiceDate(invoice?.createdAt)}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap font-medium text-slate-700">
                                {item?.acts?.label}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right">
                                {item?.quantity}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right">
                                {item?.price}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right">
                                {item?.insurerCoverage}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right">
                                {item?.totalInvoice}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-emerald-600 font-semibold">
                                {item?.invoiceAmount}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-slate-700 font-semibold">
                                {item?.amountPaid}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-right text-rose-600 font-semibold">
                                {item?.remainingPaid || 0}
                              </td>
                            </tr>
                          )
                        )}
                        <tr className="border-t border-slate-200 bg-slate-50/70 text-xs sm:text-sm text-slate-600">
                          <td
                            colSpan={2}
                            className="px-3 py-4 whitespace-nowrap"
                          >
                            <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                              Reste à payer
                            </span>
                            <div className="mt-0.5 text-base text-rose-600 font-extrabold">
                              {(() => {
                                totalRemainingPaid =
                                  invoice?.invoiceActsList?.reduce(
                                    (prev: any, curr: any) =>
                                      prev + (curr?.remainingPaid || 0),
                                    0
                                  ) || 0;
                                return totalRemainingPaid;
                              })()}{" "}
                              FCFA
                            </div>
                          </td>
                          <td
                            colSpan={2}
                            className="px-3 py-4 whitespace-nowrap"
                          >
                            <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                              Mt. brut
                            </span>
                            <div className="mt-0.5 text-base text-blue-600 font-extrabold">
                              {invoice?.invoiceActsList?.reduce(
                                (prev: any, curr: any) =>
                                  prev + (curr?.totalInvoice || 0),
                                0
                              )}{" "}
                              FCFA
                            </div>
                          </td>
                          <td
                            colSpan={2}
                            className="px-3 py-4 whitespace-nowrap text-right"
                          >
                            <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                              Mt. payé
                            </span>
                            <div className="mt-0.5 text-base text-slate-800 font-extrabold">
                              {(() => {
                                mtPayeValue =
                                  invoice?.invoiceActsList?.reduce(
                                    (prev: any, curr: any) =>
                                      prev + (curr?.amountPaid || 0),
                                    0
                                  ) || 0;
                                return mtPayeValue;
                              })()}{" "}
                              FCFA
                            </div>
                          </td>
                          <td
                            colSpan={3}
                            className="px-3 py-4 whitespace-nowrap text-right"
                          >
                            <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                              Mt. fact
                            </span>
                            <div className="mt-0.5 text-base text-emerald-600 font-extrabold">
                              {invoice?.invoiceActsList?.reduce(
                                (prev: any, curr: any) =>
                                  prev + (curr?.invoiceAmount || 0),
                                0
                              )}{" "}
                              FCFA
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        if (!invoice?.id) return;
                        openProtectedFile(
                          `/invoice/invoice-pdf/${invoice.id}`,
                          { loadingMessage: "Téléchargement de la facture…" }
                        );
                      }}
                      disabled={!invoice?.id}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-emerald-700 hover:border-emerald-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <i className="bx bx-download text-lg" />
                      Télécharger
                    </button>
                    {(() => {
                      const isPaid = paymentStatus === "PAID";
                      if (!invoice?.id || mtPayeValue <= 0) return null;
                      if (isPaid && totalRemainingPaid === 0) return null;

                      const fedapayAmount =
                        isPaid && totalRemainingPaid > 0
                          ? totalRemainingPaid
                          : mtPayeValue;

                      // Fallback aligné sur la nouvelle réponse plate du
                      // back (`patientFirstName`, `patientLastName`,
                      // `patientEmail`, `patientPhoneNumber`). L'ancien
                      // `invoice.patient.*` n'existe plus.
                      return (
                        <FedaPayButton
                          invoiceId={invoice.id}
                          amount={fedapayAmount}
                          customer={{
                            firstName:
                              current_patient?.firstName ||
                              invoice?.patientFirstName ||
                              "",
                            lastName:
                              current_patient?.lastName ||
                              invoice?.patientLastName ||
                              "",
                            email:
                              current_patient?.email ||
                              invoice?.patientEmail ||
                              "",
                            phoneNumber:
                              current_patient?.phoneNumber ||
                              invoice?.patientPhoneNumber ||
                              "",
                          }}
                          description={`Paiement Facture #${
                            invoice?.invoiceNumber || invoice?.id
                          }`}
                          disabled={isPaid && totalRemainingPaid === 0}
                          onPaymentSuccess={() => {
                            window.location.reload();
                          }}
                          onPaymentError={(error: any) => {
                            console.error("Payment error:", error);
                          }}
                        />
                      );
                    })()}
                  </div>
                </div>
              </AccordionCustom>
            );
          })
        )}
      </div>

      {selectedInvoiceId && (
        <FinancialTransactionModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedInvoiceId(null);
          }}
          invoiceId={selectedInvoiceId}
          onSuccess={() => {
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ usersReducer }: any) => ({
  current_patient: usersReducer.current_patient,
});

export default connect(mapStateToProps)(TabThree);
